import React, { useEffect, useState } from 'react';
import styles from '../scoreCardDialog.module.css';
import { CircularProgress, InputAdornment, List, ListItem, ListItemText, Paper, TextField, Typography, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import {recordScore} from "../../../../interfaces/recordScore";
import {iGolfCourse} from "../../../../interfaces/iGolfCourse";
import { iGolfCourseTee } from '../../../../interfaces/iGolfCourseTee';

export default function IgolfTextfield({ record, setCourseName, searchiGolfCourses, countryStateList, selectedCourse, setSelectedCourse, options, loading, showDialog, setiGolfTeeText, setSelectedTee }:
    { record?: recordScore, setCourseName: (courseName: string) => void, searchiGolfCourses: (courseName: string, stateList: any[]) => void, countryStateList: any[], selectedCourse: iGolfCourse | null, setSelectedCourse: (iGolfCourseId: iGolfCourse | null) => void, options: any[], loading: boolean, showDialog: boolean, setiGolfTeeText: (igolfName: string) => void, setSelectedTee: (igolfTeeSelecter: iGolfCourseTee | null) => void }) {
    const [inputValue, setInputValue] = useState('');
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isTyping, setIsTyping] = useState(false);
    const [courseFieldWidth, setCourseFieldWidth] = useState(0);

    const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(function loadRecordiGolfCourse() {
        if (record?.courseObj) {
            setSelectedCourse(record.courseObj as iGolfCourse);
        }
        if (record?.course) {
            const { course } = record;
            setInputValue(course);
            setCourseName(course);
        }
    }, [record]);

    useEffect(function setDraftCourseName() {
        if (showDialog && !record && selectedCourse) {
            setIsTyping(true);
            setInputValue(selectedCourse.courseName);
        }
        if (showDialog) {
            getSizeOfParentField();
        }
    }, [showDialog]);

    const getSizeOfParentField = () => {
        const parentField = document.getElementById('igolfCourseNameTextField');
        if (parentField) {
            const parentFieldWidth = parentField.clientWidth;
            setCourseFieldWidth(parentFieldWidth);
        }
    };

    const handleInputChange = (e: any) => {
        const value = e.target.value;
        setInputValue(value);
        setIsTyping(true);
        setSelectedCourse(null);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (value.length >= 4) {
            debounceTimeout.current = setTimeout(() => {
                setIsTyping(false);
                searchiGolfCourses(value, countryStateList);
            }, 850);
        }

        if (value.length === 0) {
            setFilteredOptions([]);
            setiGolfTeeText('');
            setSelectedTee(null);
            setSelectedCourse(null);
        }
    };

    const handleClear = () => {
        setInputValue('');
        setSelectedCourse(null);
        setiGolfTeeText('');
        setSelectedTee(null);
    };

    useEffect(() => {
        if (options && options.length > 0) {
            setFilteredOptions(options);
        }
    }, [options]);

    const highlightMatch = (text: string, query: string) => {
        if (!query) return text;
        const beforeComma = query.split(",")[0].trim();

        const regex = beforeComma ? new RegExp(`(${beforeComma.split(' ').join('|')})`, 'gi')
            : new RegExp(`(${query.split(' ').join('|')})`, 'gi');

        return text.split(regex).map((part, index) =>
            regex.test(part) ? <span style={{ fontWeight: 800 }} key={index}>{part}</span> : part
        );
    };

    const selectiGolfCourse = (course: any) => {
        setInputValue(course.courseName);
        setSelectedCourse({
            id_course: course.id_course,
            courseName: course.courseName,
            city: course.city,
            stateShort: course.stateShort,
            countryShort: course.countryShort,
            CourseNumHoles: course.layoutTotalHoles
        } as iGolfCourse);
        setCourseName(course.courseName);
        setFilteredOptions([]);
    }

    const renderOptions = (options: any[]) => {
        return options.map((option, index) => {
            return (
                <li
                    className={styles.iGolfOptions}
                    onClick={() => selectiGolfCourse(option)}
                    key={`igolf-courses-${index}`}
                    data-igolfid={option.id_course}
                >   
                    <span className={styles.courseNameiGolf}>
                        <span>{highlightMatch(option.courseName, inputValue)}</span>
                    </span>
                    <span className={styles.courseLocation}>
                        {option.length > 35 ? "" : option.length > 25 ? option.city.substring(0, 10) + "...," : `${option.city}`}
                        {option?.stateShort ? `, ${option?.stateShort}` : ""}
                    </span>
                </li>
            );
        });
    }

    return (
        <div>
            <Typography className={styles.inputLabel}>
                Course Name
            </Typography>
            <TextField
                id='igolfCourseNameTextField'
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                autoComplete="off"
                variant="outlined"
                size="small"
                placeholder="Enter course name"
                required
                InputProps={{
                    endAdornment: inputValue && (
                        <InputAdornment position="end">
                            <IconButton onClick={handleClear} edge="end">
                                <ClearIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiInputBase-input': {
                        textTransform: 'uppercase',
                        fontFamily: 'Repro Mono',
                        color: '#7B726C',
                    },
                    '& .MuiOutlinedInput-root': {
                        height: '53px',
                        '& fieldset': {
                            borderColor: '#ECE9E4',
                        },
                        '&:hover fieldset': {
                            borderColor: '#7b726c',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#7b726c',
                        },
                    },
                    '& .MuiInputBase-input::placeholder': {
                        textTransform: 'uppercase',
                        opacity: 1,
                    },
                }}
            />  
            {loading ? (
                <Paper className={`${styles.iGolfListItem}`} style={{ width: `${courseFieldWidth}px` }}>
                    <div className={`${styles.loadingiGolf} ${styles.iGolfLoading}`}>
                        <CircularProgress size={'25px'} sx={{ color: '#FD3300' }}/>
                    </div>
                </Paper>
            ) : !isTyping && inputValue && filteredOptions.length > 0 ? (
                <Paper className={styles.iGolfListItem} style={{ width: `${courseFieldWidth}px` }}>
                    <List>
                        {renderOptions(filteredOptions)}
                    </List>
                </Paper>
            ) : !isTyping && inputValue && !selectedCourse && filteredOptions.length === 0 ? (
                <Paper className={`${styles.iGolfListItem}`} style={{ width: `${courseFieldWidth}px` }}>
                    <ListItem>
                        <ListItemText primary="No courses found" />
                    </ListItem>
                </Paper>
            ) : null}
        </div>
    );
}
